import { useEffect, useState } from "react";
import { Share2, Copy, RefreshCw, Check, ClipboardList, LineChart, Layers } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { fetchPartage, setPartage, regenererPartage } from "@/lib/questionnaire";

export default function PartageClientCard({ dossierId, showPrevisionnel = true }) {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [copiedKey, setCopiedKey] = useState(null);

    useEffect(() => {
        fetchPartage(dossierId).then(setState).catch((e) => toast.error("Partage client", { description: e.message }));
    }, [dossierId]);

    const token = state?.token_partage;
    const origin = window.location.origin;
    const urlQuestionnaire = token ? `${origin}/portail/${token}` : "";
    const urlPrevisionnel = token ? `${origin}/client/${token}/previsionnel` : "";
    const urlCombine = token ? `${origin}/portail/${token}?flow=combined` : "";

    async function toggle(actif) {
        setBusy(true);
        try {
            setState(await setPartage(dossierId, actif));
            toast.success(actif ? "Portail client activé" : "Portail client désactivé");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    }

    async function copy(key, u, label) {
        try {
            await navigator.clipboard.writeText(u);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2000);
            toast.success(label);
        } catch {
            toast.error("Copie impossible", { description: u });
        }
    }

    async function regen() {
        if (!window.confirm("Régénérer le lien ? Tous les liens client (questionnaire, prévisionnel et combiné) partagent le même jeton : les anciens liens cesseront immédiatement de fonctionner.")) return;
        setBusy(true);
        try {
            setState(await regenererPartage(dossierId));
            toast.success("Nouveau lien généré");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    }

    const links = [
        {
            key: "questionnaire",
            testid: "questionnaire",
            icon: ClipboardList,
            title: "Questionnaire seul",
            desc: "Formulaire qualitatif + dépôt de pièces. Se termine par une page de remerciement.",
            url: urlQuestionnaire,
            label: "Lien Questionnaire seul copié",
            show: true,
        },
        {
            key: "previsionnel",
            testid: "previsionnel",
            icon: LineChart,
            title: "Prévisionnel seul",
            desc: "Grille financière destinée au service comptable.",
            url: urlPrevisionnel,
            label: "Lien Prévisionnel seul copié",
            show: showPrevisionnel,
        },
        {
            key: "combine",
            testid: "combine",
            icon: Layers,
            title: "Combiné (Questionnaire + Prévisionnel)",
            desc: "Flux complet : à la soumission du questionnaire, le client est redirigé vers le prévisionnel.",
            url: urlCombine,
            label: "Lien Combiné copié",
            show: showPrevisionnel,
        },
    ].filter((l) => l.show);

    return (
        <Card data-testid="partage-client-card" className="border-slate-200 bg-white shadow-sm">
            <CardContent className="pt-6 space-y-5">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center">
                            <Share2 className="h-4 w-4 text-primary" strokeWidth={1.75} />
                        </div>
                        <div>
                            <h2 className="font-display text-xl text-foreground">Générer le lien d&apos;accès client</h2>
                            <p className="text-xs text-muted-foreground">Liens magiques sans login. Choisissez le parcours adapté à votre client (dirigeant, service comptable, ou les deux). Aucune donnée financière ni synthèse n&apos;est visible côté client.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span data-testid="partage-status" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{state?.partage_actif ? "Actif" : "Inactif"}</span>
                        <Switch data-testid="partage-toggle" checked={!!state?.partage_actif} onCheckedChange={toggle} disabled={busy || !state} />
                    </div>
                </div>
                {state && (
                    <div className="space-y-3">
                        {links.map((l) => {
                            const Icon = l.icon;
                            const active = state.partage_actif;
                            return (
                                <div key={l.key} data-testid={`partage-block-${l.testid}`} className="rounded-xl border border-border bg-background/60 p-4 space-y-2">
                                    <div className="flex items-start gap-3">
                                        <div className="h-8 w-8 shrink-0 rounded-lg bg-accent flex items-center justify-center">
                                            <Icon className="h-4 w-4 text-primary" strokeWidth={1.75} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-foreground">{l.title}</p>
                                            <p className="text-xs text-muted-foreground">{l.desc}</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 pl-11">
                                        <a data-testid={`partage-url-${l.testid}`} href={l.url} target="_blank" rel="noopener noreferrer" className={`flex-1 min-w-[220px] truncate rounded-md border border-border bg-background px-3 py-2 text-xs font-mono ${active ? "text-indigo-700 hover:underline" : "text-muted-foreground line-through pointer-events-none"}`}>{l.url}</a>
                                        <Button variant="outline" size="sm" data-testid={`partage-copy-${l.testid}`} onClick={() => copy(l.key, l.url, l.label)} disabled={!active} className="rounded-full border-primary/20 text-primary hover:bg-accent">
                                            {copiedKey === l.key ? <Check className="h-4 w-4" strokeWidth={2} /> : <Copy className="h-4 w-4" strokeWidth={1.75} />} Copier le lien
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                        <div className="flex justify-end pt-1">
                            <Button variant="ghost" size="sm" data-testid="partage-regen-button" onClick={regen} disabled={busy} className="rounded-full text-muted-foreground">
                                <RefreshCw className="h-4 w-4" strokeWidth={1.75} /> Régénérer les liens
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
